import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Box,
  Avatar,
  IconButton,
} from '@mui/material';
import { ORGANIZATION_SERVER } from '../../Config';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import ImageIcon from '@mui/icons-material/Image'; // 원하면 import 생략 가능

const CreateProjectDialog = (props) => {
  const { enqueueSnackbar } = useSnackbar();

  const [projectName, setProjectName] = useState('');
  const [name, setName] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const isValid = projectName.trim() !== '';

  // 취소 버튼용 핸들러: 상태 초기화 후 props handleClose 호출
  const handleCancel = () => {
    setProjectName('');
    setName('');
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImageFile(null);
    setImagePreview(null);
    props.handleClose(); // <- 상위 컴포넌트(handleClose) 호출
  };

  // 파일 선택 핸들러
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      enqueueSnackbar('이미지 파일만 업로드 가능합니다.', { variant: 'warning' });
      return;
    }

    // 3MB 제한 (3 * 1024 * 1024)
    const maxSizeInBytes = 3 * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      enqueueSnackbar('이미지 파일은 3MB 이하로 선택해 주세요.', { variant: 'warning' });
      return; // 업로드 중단
    }

    // 이전 미리보기 URL 해제
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }

    setImageFile(file);
    const objectUrl = URL.createObjectURL(file);
    setImagePreview(objectUrl);
  };

  // submit 시 data 구성
  const submit = () => {
    if (!isValid) {
      enqueueSnackbar('프로젝트명을 입력해 주세요.', {
        variant: 'warning',
        autoHideDuration: 3000,
      });
      return;
    }

    const data = new FormData(); // 이미지를 보낼 때는 보통 FormData로
    data.append('projectName', projectName.trim());
    data.append('name', name.trim());

    if (imageFile) {
      data.append('image', imageFile);
    }

    axios
      .post(`${process.env.REACT_APP_SERVER}${ORGANIZATION_SERVER}/project`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(() => {
        props.projectLoad();
        props.handleClose();
      })
      .catch(() => {
        enqueueSnackbar('프로젝트 생성에 실패했습니다.', { variant: 'error' });
      });
  };

  // 미리보기 제거
  const removeImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImageFile(null);
    setImagePreview(null);
  };

  useEffect(() => {
    if (!props.open) {
      // Dialog 닫힐 때 상태 초기화
      setProjectName('');
      setName('');
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
      setImageFile(null);
      setImagePreview(null);
    }
  }, [props.open]);

  return (
    <Dialog open={props.open} onClose={props.handleClose} maxWidth="sm" fullWidth>
      <DialogTitle fontWeight="bold" sx={{ px: 3, pt: 2.5 }}>
        프로젝트 생성
      </DialogTitle>
      <DialogContent dividers sx={{ px: 3, py: 2 }}>
        {/* 프로젝트명 */}
        <Typography variant="subtitle2" component="div" sx={{ mb: 0.5, fontSize: '1rem', fontWeight: 500 }}>
          과제 기관명<span style={{ color: 'red' }}>*</span>
        </Typography>
        <TextField
          autoFocus
          fullWidth
          placeholder="과제 기관 이름을 입력하세요"
          value={name}
          onChange={(e) => setName(e.target.value)}
          variant="outlined"
          size="small"
          error={!isValid && name.trim() === ''}
          helperText={!isValid && name.trim() === '' ? '필수 입력 항목입니다' : ' '}
          sx={{ '& .MuiFormHelperText-root': { color: 'error.main' } }}
        />

        {/* 설명 */}
        <Typography variant="subtitle2" component="div" sx={{ mb: 0.5, mt: 2, fontSize: '1rem', fontWeight: 500 }}>
          과제 설명
        </Typography>
        <TextField
          fullWidth
          placeholder="과제명 또는 과제에 대한 설명을 입력하세요"
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          variant="outlined"
          size="small"
          multiline
          rows={3}
        />

        {/* 이미지 업로드 + 미리보기 */}
        <Typography variant="subtitle2" component="div" sx={{ mt: 2, fontSize: '1rem', fontWeight: 500 }}>
          프로젝트 로고 이미지
        </Typography>
        <Typography variant="subtitle2" component="div" sx={{ mb: 0.5, fontSize: '0.7rem', color: 'red' }}>
          로고 이미지 파일은 3MB 이하
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Button variant="contained" component="label" size="small" color="secondary" startIcon={<ImageIcon />}>
            이미지 선택
            <input type="file" accept="image/*" hidden onChange={handleImageChange} />
          </Button>

          {imageFile && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 1,
              }}
            >
              <Avatar sx={{ width: 64, height: 64 }} src={imagePreview} alt="Upload preview" />
              <Box>
                <Typography variant="body2">{imageFile.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {(imageFile.size / 1024).toFixed(0)} KB
                </Typography>
              </Box>
              <IconButton size="small" onClick={removeImage} color="error">
                <span style={{ fontSize: '16px' }}>&times;</span>
              </IconButton>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" color="inherit" onClick={handleCancel}>
          취소
        </Button>
        <Button variant="contained" color="primary" disabled={!isValid} onClick={submit}>
          프로젝트 생성
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProjectDialog;
